/**
 * Account Tab Mixin
 *
 * Factory function that returns account state and methods for embedding
 * inside an entity slideover (landlord or tenancy). Spread into the
 * entity component alongside legacyImportMixin and transactionSearchMixin.
 *
 * Usage:
 *   import { accountTabMixin } from './account_tab_mixin'
 *   return { ...accountTabMixin('landlord'), ...legacyImportMixin('landlord'), ...transactionSearchMixin('landlord'), ...otherState }
 */

const PLURALS = { landlord: 'landlords', tenancy: 'tenancies', tenancy_application: 'tenancy_applications' }

export function accountTabMixin(accountableType) {
  const pluralType = PLURALS[accountableType] || `${accountableType}s`
  const ID_PROPS = { tenancy: 'tenancyId', landlord: 'landlordId', tenancy_application: 'applicationId' }
  const idProp = ID_PROPS[accountableType] || `${accountableType}Id`

  return {
    // Account tab state (prefixed where collision risk exists)
    acctLoading: false,
    acctError: null,
    acctStats: null,
    acctDataLoaded: false,

    // These names are required by transactionSearchMixin / legacyImportMixin
    transactions: [],
    page: 1,
    hasMore: false,
    loadingMore: false,

    // Type-specific data
    rentThisMonth: [],   // landlord
    rentSchedule: [],    // tenancy
    rentHistory: [],     // tenancy
    invoices: [],        // contractor

    // UI state
    showBackToTop: false,
    menuOpen: false,

    // Initialise account tab — call from the entity's init()
    initAccountTab() {
      const self = this

      // Refresh the parent entity (landlord/tenancy/contractor) so header
      // stat cards stay in sync after balance-affecting operations.
      const refreshParent = () => {
        if (accountableType === 'tenancy' && self.fetchTenancy) self.fetchTenancy(true)
        else if (accountableType === 'landlord' && self.fetchLandlord) self.fetchLandlord(true)
        else if (accountableType === 'contractor' && self.fetchContractor) self.fetchContractor(true)
        else if (accountableType === 'tenancy_application' && self.fetchApplication) self.fetchApplication(true)
      }

      window.addEventListener('charge-created', () => {
        if (self.acctDataLoaded) self.refreshAccountData()
        refreshParent()
      })
      window.addEventListener('refund-created', () => {
        if (self.acctDataLoaded) self.refreshAccountData()
        refreshParent()
      })
      window.addEventListener('rent-refund-created', () => {
        if (self.acctDataLoaded) self.refreshAccountData()
        refreshParent()
      })
      window.addEventListener('account-updated', (e) => {
        const TYPE_MAP = { landlord: 'Landlord', tenancy: 'Tenancy', tenancy_application: 'TenancyApplication', contractor: 'Contractor' }
        const expectedType = TYPE_MAP[accountableType] || accountableType
        if (e.detail?.accountable_type === expectedType && e.detail?.accountable_id === self[idProp]) {
          if (self.acctDataLoaded) self.refreshAccountData()
          refreshParent()
        }
      })
      window.addEventListener(`refresh-${accountableType}-account`, (e) => {
        if (self.acctDataLoaded && String(e.detail?.id) === String(self[idProp])) {
          self.refreshAccountData()
        }
      })
      window.addEventListener('scroll-to-transaction', (e) => {
        if (self.activeTab === 'account' && e.detail?.id) self.scrollToTransaction(e.detail.id)
      })
    },

    // Fetch account data from the accounting controller
    async fetchAccountData(initial = false) {
      if (initial) {
        this.acctLoading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.acctError = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/${pluralType}/${this[idProp]}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        this.acctStats = data.stats

        if (accountableType === 'landlord') {
          this.rentThisMonth = data.rent_this_month || []
          this.landlordInvoices = data.invoices || []
        } else if (accountableType === 'contractor') {
          this.invoices = data.invoices || []
        } else {
          this.rentSchedule = data.rent_schedule || []
          this.rentHistory = data.rent_history || []
        }

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
        this.acctDataLoaded = true
      } catch (error) {
        console.error(`Failed to fetch ${accountableType} account:`, error)
        this.acctError = `Failed to load account details. Please try again.`
      } finally {
        this.acctLoading = false
        this.loadingMore = false
      }
    },

    // Silent refresh — keeps existing content visible while fetching updated data
    async refreshAccountData() {
      this.acctError = null

      try {
        const params = new URLSearchParams()
        params.set('page', 1)

        const response = await fetch(`/accounting/accounts/${pluralType}/${this[idProp]}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const data = await response.json()
        this.acctStats = data.stats

        if (accountableType === 'landlord') {
          this.rentThisMonth = data.rent_this_month || []
          this.landlordInvoices = data.invoices || []
        } else if (accountableType === 'contractor') {
          this.invoices = data.invoices || []
        } else {
          this.rentSchedule = data.rent_schedule || []
          this.rentHistory = data.rent_history || []
        }

        this.transactions = data.transactions?.lines || []
        this.page = 1
        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error(`Failed to refresh ${accountableType} account:`, error)
      }
    },

    // Alias for legacyImportMixin compatibility
    // Also silently refreshes the parent entity to update header stat cards
    refreshData() {
      this.refreshAccountData()
      if (accountableType === 'tenancy' && this.fetchTenancy) {
        this.fetchTenancy(true)
      } else if (accountableType === 'landlord' && this.fetchLandlord) {
        this.fetchLandlord(true)
      } else if (accountableType === 'contractor' && this.fetchContractor) {
        this.fetchContractor(true)
      }
    },

    // Load next page (transactions only)
    loadMoreTransactions() {
      if (this.loadingMore || !this.hasMore) return
      this.page++
      this.fetchAccountData(false)
    },

    // Infinite scroll handler — only fires when Account tab is active
    handleAccountScroll(event) {
      this.showBackToTop = window.shouldShowBackToTop(event)

      const el = event.target
      const scrollPosition = el.scrollTop + el.clientHeight
      const threshold = el.scrollHeight - 200

      if (scrollPosition >= threshold && this.hasMore && !this.loadingMore) {
        this.loadMoreTransactions()
      }
    },

    scrollAccountToTop() {
      window.scrollToTopHelper(this.$refs.scrollContainer)
    },

    // Reset account state — call from the entity's close()
    resetAccountState() {
      this.acctDataLoaded = false
      this.acctLoading = false
      this.acctError = null
      this.acctStats = null
      this.transactions = []
      this.page = 1
      this.hasMore = false
      this.loadingMore = false
      this.rentThisMonth = []
      this.rentSchedule = []
      this.rentHistory = []
      this.invoices = []
      this.showBackToTop = false
      this.menuOpen = false
    },

    // ========== Format helpers ==========
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const isNegative = pence < 0
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return (isNegative ? '-' : '') + '\u00A3' + formatted
    },

    formatSignedPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const prefix = pence >= 0 ? '+' : '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${prefix}\u00A3${formatted}`
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    txnBarClass(txn) {
      if (txn.payment_status === 'unreconciled') return 'bg-amber-400'
      if (txn.charge_status === 'unsettled' || txn.charge_status === 'outstanding' || txn.charge_status === 'partial') return 'bg-amber-400'
      return 'bg-blue-400'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500 dark:text-gray-400'
    },

    // ========== Rent-history calendar heatmap (tenancy) ==========
    // Group entries by year, slot each into its calendar month column so
    // years line up visually. Empty cells render as transparent gaps —
    // pre-tenancy and future months are not rendered at all.
    // Defined as a method (not getter) because spread-into-component flattens
    // getters at spread time; methods stay reactive when called from x-for.
    heatmapYears() {
      if (!this.rentHistory || this.rentHistory.length === 0) return []
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const monthIndex = Object.fromEntries(months.map((m, i) => [m, i]))

      const byYear = new Map()
      let lastYM = -Infinity
      for (const entry of this.rentHistory) {
        const [monthStr, yearStr] = (entry.month || '').split(' ')
        const year = parseInt(yearStr, 10)
        const m = monthIndex[monthStr]
        if (m === undefined || Number.isNaN(year)) continue
        if (!byYear.has(year)) byYear.set(year, new Array(12).fill(null))
        byYear.get(year)[m] = entry
        const ym = year * 12 + m
        if (ym > lastYM) lastYM = ym
      }

      // Oldest year first — top-left is the very first month, bottom-right
      // is the most recent. Mark null cells *before* the latest known
      // entry as placeholders so the year row reads as a continuous grid;
      // null cells *after* the latest entry stay invisible (those are
      // future months we haven't billed yet, not gaps in our records).
      return [...byYear.keys()]
        .sort((a, b) => a - b)
        .map(year => {
          const cells = byYear.get(year).map((cell, idx) => {
            if (cell) return cell
            return (year * 12 + idx) < lastYM ? { _placeholder: true } : null
          })
          return { year, cells }
        })
    },

    // One cell of the rent-history grid, in the design language's palette:
    // bone for a month paid on time, the amber pill ground for a little late
    // or partial, the red pill ground for 15+ days late or unpaid. A month
    // before the records start is a dashed outline; a future month is empty.
    heatmapCellClass(cell) {
      const base = 'h-[22px] rounded-[4px] select-none'
      if (!cell) return `${base} bg-transparent`
      if (cell._placeholder) return `${base} border border-dashed border-[#E7E4DB]`
      if (cell.status === 'received') return `${base} bg-[#E7E4DB]`
      if (cell.status === 'late') return cell.days_late <= 14 ? `${base} bg-[#FBEBD2]` : `${base} bg-[#F9E1E1]`
      if (cell.status === 'partial') return `${base} bg-[#FBEBD2]`
      if (cell.status === 'outstanding') return `${base} bg-[#F9E1E1]`
      return `${base} bg-[#E7E4DB]`
    },

    heatmapCellTitle(cell) {
      if (!cell || cell._placeholder) return ''
      const parts = [cell.month, this.formatPence(cell.amount_pence), cell.status_label]
      if (cell.rent_increase_pence) parts.push(`rent up ${this.formatRentIncrease(cell.rent_increase_pence)}`)
      return parts.join(' · ')
    },

    // The rent increases the grid marks, oldest first, for the legend:
    // [{ month: 'Oct 2025', amount: '£50' }]
    heatmapRises() {
      const seen = new Set()
      const rises = []
      for (const row of this.heatmapYears()) {
        for (const cell of row.cells) {
          if (!cell || cell._placeholder || !cell.rent_increase_pence || seen.has(cell.month)) continue
          seen.add(cell.month)
          rises.push({ month: cell.month, amount: this.formatRentIncrease(cell.rent_increase_pence) })
        }
      }
      return rises
    },

    // Short month label inside the cell — e.g. "Mar"
    heatmapCellMonth(cell) {
      if (!cell) return ''
      return (cell.month || '').split(' ')[0]
    },

    // Tiny status hint under the month — ✓ on time, +Nd late, ! unpaid, ◐ partial
    heatmapCellHint(cell) {
      if (!cell) return ''
      if (cell.status === 'received') return '✓'
      if (cell.status === 'late') return `+${cell.days_late}d`
      if (cell.status === 'partial') return '◐'
      if (cell.status === 'outstanding') return '!'
      return ''
    },

    // "£45" / "£12.50" — format a pence delta for the rent-increase badge.
    formatRentIncrease(pence) {
      if (pence == null) return ''
      const pounds = pence / 100
      const decimals = pence % 100 === 0 ? 0 : 2
      return `£${pounds.toFixed(decimals)}`
    },

    // ========== Stat card getters ==========
    get acctStatValueClass() {
      if (!this.acctStats) return 'text-2xl'
      let lengths
      if (accountableType === 'landlord') {
        lengths = [
          this.formatPence(this.acctStats.balance_held_pence).length,
          this.formatPence(this.acctStats.rent_outstanding_pence).length,
          this.formatPence(this.acctStats.reserve_pence).length
        ]
      } else {
        lengths = [
          this.formatPence(this.acctStats.balance_pence).length,
          this.formatPence(this.acctStats.monthly_rent_pence).length,
          this.formatPence(this.acctStats.deposit_pence).length
        ]
      }
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    get acctBalanceSubtitle() {
      if (!this.acctStats) return ''
      if (accountableType === 'landlord') {
        if (this.acctStats.balance_held_pence < 0) return 'Money is owed'
        if (this.acctStats.balance_held_pence === 0) return 'Nothing to pay'
        return 'Held on account'
      } else {
        const status = this.acctStats.balance_status
        if (status === 'clear') return 'Clear'
        if (status === 'overdue') return 'Overdue'
        return 'In credit'
      }
    },

    // Landlord-specific
    get acctRentOutstandingSubtitle() {
      if (!this.acctStats) return ''
      const count = this.acctStats.rent_outstanding_property_count || 0
      const total = this.acctStats.rent_outstanding_total_properties || 0
      if (this.acctStats.rent_outstanding_pence === 0) return 'All received'
      return `${count} of ${total} ${total === 1 ? 'property' : 'properties'}`
    },

    get acctReserveSubtitle() {
      if (!this.acctStats) return ''
      return this.acctStats.reserve_pence === 0 ? 'No reserve held' : 'Withheld for costs'
    },

    // Tenancy-specific
    get acctRentDueSubtitle() {
      if (!this.acctStats) return ''
      const day = this.acctStats.rent_due_day || 1
      const ordinal = this._ordinalSuffix(day)
      return `Due ${day}${ordinal} of each month`
    },

    get acctDepositSubtitle() {
      if (!this.acctStats) return ''
      return this.acctStats.deposit_subtitle || 'N/A'
    },

    // Balance styling (tenancy)
    get acctBalanceBorderClass() {
      if (!this.acctStats) return 'border-gray-800 dark:border-gray-200'
      if (this.acctStats.balance_status === 'overdue') return 'border-red-500'
      return 'border-gray-800 dark:border-gray-200'
    },

    get acctBalanceValueClass() {
      if (!this.acctStats) return 'text-gray-900 dark:text-white'
      if (this.acctStats.balance_status === 'overdue') return 'text-red-600 dark:text-red-400'
      return 'text-gray-900 dark:text-white'
    },

    get acctBalanceLabelClass() {
      if (!this.acctStats) return 'text-gray-600'
      if (this.acctStats.balance_status === 'overdue') return 'text-red-600 dark:text-red-400'
      return 'text-gray-600'
    },

    // Current month/year display
    get currentMonthYear() {
      const date = new Date()
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${months[date.getMonth()]} ${date.getFullYear()}`
    },

    // ========== Client value card helpers (landlord) ==========
    // Defined as methods (not getters) because spread-into-component flattens
    // getters at spread time; methods stay reactive when called from x-text.
    // Reads from landlord.client_value (single source of truth, populated by
    // the landlord JSON via ClientValueSummary).
    clientValueTenureDisplay() {
      const cv = this.landlord?.client_value
      if (!cv) return '—'
      const months = cv.tenure_months || 0
      if (months < 12) return `${months} mo`
      const years = months / 12
      // "8.2 yrs" — one decimal place, trim trailing .0
      const rounded = Math.round(years * 10) / 10
      const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)
      return `${text} yrs`
    },

    // Cumulative-fees series in viewBox coordinates (280 × 64, with a 16px
    // top inset so the line's peak doesn't touch the top edge of the chart).
    clientValueChartPoints() {
      const cv = this.landlord?.client_value
      if (!cv?.monthly_fees_12m?.length) return []
      const series = cv.monthly_fees_12m
      let running = 0
      const cumulative = series.map(b => (running += (b.fees_pence || 0)))
      const max = Math.max(...cumulative, 1) // avoid /0 for all-zero series
      const W = 280
      const H = 64
      const TOP = 16
      const usable = H - TOP
      const stepX = series.length > 1 ? W / (series.length - 1) : 0
      return cumulative.map((v, i) => ({
        x: +(i * stepX).toFixed(2),
        y: +(H - (v / max) * usable).toFixed(2)
      }))
    },

    clientValueLinePath() {
      const pts = this.clientValueChartPoints()
      if (pts.length === 0) return ''
      return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    },

    clientValueAreaPath() {
      const pts = this.clientValueChartPoints()
      if (pts.length === 0) return ''
      const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
      return `${line} L280,64 L0,64 Z`
    },

    clientValueEndPoint() {
      const pts = this.clientValueChartPoints()
      return pts.length ? pts[pts.length - 1] : { x: 0, y: 0 }
    },

    _ordinalSuffix(day) {
      if (day >= 11 && day <= 13) return 'th'
      switch (day % 10) {
        case 1: return 'st'
        case 2: return 'nd'
        case 3: return 'rd'
        default: return 'th'
      }
    }
  }
}
